import { z } from 'zod';

// ---------------------------------------------------------------------
// Sections
// ---------------------------------------------------------------------
export type SectionCode =
  | 'FIELD'
  | 'LABOUR'
  | 'MATERIAL'
  | 'EQUIPMENT'
  | 'SUBCONTRACTOR'
  | 'ENGINEERING'
  | 'FREIGHT'
  | 'MISC';

export type Section = {
  id: number;
  code: SectionCode | string;
  name: string;
  is_active: boolean;
  display_order: number;
};

// ---------------------------------------------------------------------
// Budget summary
// ---------------------------------------------------------------------
export type SectionTotal = {
  section_code: string;
  section_name: string;
  original: string;
  revised: string;
  /** Sum of planned (pending/ROM/submitted CO) allocations for this section. */
  planned: string;
  /** revised + planned — what the section's revised would be if all planned
   *  pending COs were approved as currently allocated. */
  projected_revised: string;
  job_to_date: string;
  variance: string;
};

export type BudgetSummary = {
  id: number;
  project: number;
  name: string;
  status: 'draft' | 'active' | 'archived';
  original_budget_total: string;
  current_budget_total: string;
  approved_change_order_total: string;
  pending_change_order_total: string;
  unallocated_approved_co_total: string;
  /** Sum of allocations attached to APPROVED change orders. */
  allocated_approved_co_total: string;
  /** Sum of allocations attached to ROM/Submitted change orders. */
  planned_pending_co_total: string;
  /** pending_change_order_total - planned_pending_co_total (>= 0). */
  unplanned_pending_co_total: string;
  /** current_budget_total + planned_pending_co_total. */
  projected_current_budget_total: string;
  actual_cost_to_date: string;
  projected_cost_to_complete: string;
  current_variance: string;
  active_version: number | null;
  active_version_number: number | null;
  field_total: string;
  labour_total: string;
  field_planned_total: string;
  labour_planned_total: string;
  field_projected_total: string;
  labour_projected_total: string;
  section_totals: SectionTotal[];
  has_unallocated_approved_cos: boolean;
  has_unplanned_pending_cos: boolean;
  created_at: string;
  updated_at: string;
};

// ---------------------------------------------------------------------
// Budget line
// ---------------------------------------------------------------------
export type BudgetLine = {
  id: number;
  budget: number;
  project: number;
  section: number;
  section_code: string;
  section_name: string;
  cost_code: string;
  cost_code_name: string;
  cost_type: string;
  description: string;
  period_to_date: string;
  job_to_date: string;
  original_estimate: string;
  revised_estimate: string;
  percent_of_estimate: string;
  actual_percent_complete: string;
  projected_cost_to_complete: string;
  projected_percent_of_estimate: string;
  current_variance: string;
  date_reported: string | null;
  notes: string;
  source_type: 'MANUAL' | 'IMPORT' | 'CHANGE_ORDER' | 'SYSTEM';
  linked_change_order: number | null;
  linked_change_order_cp: string;
  created_by: number | null;
  updated_by: number | null;
  created_at: string;
  updated_at: string;
};

export const lineWriteSchema = z.object({
  section: z.coerce.number({ error: 'Section is required' }),
  cost_code: z.string().trim().min(1, { message: 'Cost code is required' }),
  cost_code_name: z.string().optional().default(''),
  cost_type: z.string().optional().default(''),
  description: z.string().optional().default(''),
  period_to_date: z.string().optional().default('0'),
  job_to_date: z.string().optional().default('0'),
  original_estimate: z.string().optional().default('0'),
  revised_estimate: z.string().optional().default('0'),
  percent_of_estimate: z.string().optional().default('0'),
  actual_percent_complete: z.string().optional().default('0'),
  projected_cost_to_complete: z.string().optional().default('0'),
  projected_percent_of_estimate: z.string().optional().default('0'),
  current_variance: z.string().optional().default('0'),
  date_reported: z.string().optional().nullable(),
  notes: z.string().optional().default(''),
  source_type: z
    .enum(['MANUAL', 'IMPORT', 'CHANGE_ORDER', 'SYSTEM'])
    .optional()
    .default('MANUAL'),
  linked_change_order: z.coerce.number().optional().nullable(),
});
export type LineWriteInput = z.input<typeof lineWriteSchema>;

// ---------------------------------------------------------------------
// Versions / activity / compare
// ---------------------------------------------------------------------
export type BudgetVersion = {
  id: number;
  version_number: number;
  label: string;
  reason:
    | 'INITIAL'
    | 'MANUAL_REVISION'
    | 'IMPORT_REVISION'
    | 'CHANGE_ORDER_APPROVED'
    | 'CHANGE_ORDER_REVERSED'
    | 'CO_ALLOCATED'
    | 'SYSTEM_RECALC'
    | 'LINE_ADDED'
    | 'LINE_EDITED'
    | 'LINE_DELETED'
    | 'LINES_SAVED';
  triggered_by_change_order: number | null;
  triggered_by_change_order_cp: string;
  triggered_by_import: number | null;
  snapshot_at: string;
  total_original_estimate: string;
  total_revised_estimate: string;
  total_job_to_date: string;
  total_variance: string;
  approved_change_order_total_at_snapshot: string;
  pending_change_order_total_at_snapshot: string;
  unallocated_approved_co_total_at_snapshot: string;
  created_by: number | null;
  created_at: string;
};

export type BudgetVersionLine = {
  id: number;
  source_line_id: number | null;
  section_code: string;
  section_name: string;
  cost_code: string;
  cost_code_name: string;
  cost_type: string;
  description: string;
  period_to_date: string;
  job_to_date: string;
  original_estimate: string;
  revised_estimate: string;
  percent_of_estimate: string;
  actual_percent_complete: string;
  projected_cost_to_complete: string;
  projected_percent_of_estimate: string;
  current_variance: string;
  date_reported: string | null;
  notes: string;
  source_type: string;
  linked_change_order_id: number | null;
  linked_change_order_cp_number: string;
  captured_at: string;
};

export type BudgetVersionDetail = BudgetVersion & {
  lines: BudgetVersionLine[];
};

export type BudgetActivity = {
  id: number;
  activity_type: string;
  description: string;
  metadata: Record<string, unknown> | null;
  user: number | null;
  user_display: string | null;
  line: number | null;
  version: number | null;
  change_order: number | null;
  change_order_cp: string;
  import_record: number | null;
  allocation: number | null;
  created_at: string;
};

export type CompareResponse = {
  from_version: BudgetVersion;
  to_version: BudgetVersion;
  totals_delta: Record<string, string>;
  rows: Array<{
    section_code: string;
    cost_code: string;
    cost_code_name: string;
    from: BudgetVersionLine | null;
    to: BudgetVersionLine | null;
    delta: Record<string, string>;
    status: 'added' | 'removed' | 'changed';
  }>;
};

// ---------------------------------------------------------------------
// Change Order ↔ Budget
// ---------------------------------------------------------------------
export type COImpactRow = {
  id: number;
  cp_number: string;
  title: string;
  status: string;
  submitted_amount: string;
  approved_amount: string;
  allocated_amount: string;
  unallocated_amount: string;
  budget_impact: string;
  all_profit: boolean;
  split_note: string;
  linked_lines: Array<{
    allocation_id: number | null;
    line_id: number | null;
    cost_code: string | null;
    cost_code_name: string | null;
    section_code: string | null;
    amount: string;
    note?: string;
    needs_review: boolean;
    is_tag_only?: boolean;
  }>;
  approved_at: string | null;
  rejected_at: string | null;
  submitted_at: string | null;
  updated_at: string;
};

export type COImpactResponse = {
  groups: {
    pending: COImpactRow[];
    approved_allocated: COImpactRow[];
    approved_unallocated: COImpactRow[];
    rejected_or_reversed: COImpactRow[];
  };
  totals: {
    approved_change_order_total: string;
    pending_change_order_total: string;
    allocated_approved_co_total: string;
    planned_pending_allocation_total: string;
    unplanned_pending_co_total: string;
    unallocated_approved_co_total: string;
    projected_current_budget_total: string;
  };
};

export const allocationLineSchema = z.object({
  line_id: z.coerce.number().optional().nullable(),
  new_line: z
    .object({
      section: z.string(),
      cost_code: z.string().optional(),
      cost_code_name: z.string().optional(),
      cost_type: z.string().optional(),
      description: z.string().optional(),
      notes: z.string().optional(),
    })
    .optional(),
  amount: z.string().min(1, { message: 'Amount required' }),
  note: z.string().optional().default(''),
});

export const allocationRequestSchema = z.object({
  allocations: z.array(allocationLineSchema),
  override_limit: z.boolean().optional().default(false),
  replace_existing: z.boolean().optional().default(false),
  all_profit: z.boolean().optional().default(false),
  split_note: z.string().max(255).optional().default(''),
});

export type AllocationRequest = z.infer<typeof allocationRequestSchema>;

// ---------------------------------------------------------------------
// Imports
// ---------------------------------------------------------------------
export type BudgetImport = {
  id: number;
  budget: number;
  project: number;
  file: string;
  file_name: string;
  status:
    'uploaded' | 'parsing' | 'parsed' | 'previewed' | 'committed' | 'failed';
  parsed_rows: Array<Record<string, string | null>> | null;
  validation_errors: Array<{ row: number; errors: string[] }> | null;
  column_mapping: Record<string, string> | null;
  target_section: number | null;
  total_preview: string | null;
  committed_version: number | null;
  error_message: string;
  created_by: number | null;
  created_at: string;
  updated_at: string;
};

export type MappingTemplate = {
  id: number;
  name: string;
  scope: 'global' | 'company' | 'project';
  project: number | null;
  mapping: Record<string, string>;
  created_by: number | null;
  created_at: string;
  updated_at: string;
};

// ---------------------------------------------------------------------
// J/C Job Cost Code Detail Report — multi-file batch import
// ---------------------------------------------------------------------
export type JcImportSource = 'KAR' | 'KIV' | 'UNKNOWN';

export type JcImportProposedAction =
  'create' | 'update' | 'skip_duplicate' | 'conflict';

export type JcCommitMode =
  | 'merge_create_only'
  | 'merge_update_existing'
  | 'replace_budget_for_selected_sources';

export type JcParsedLine = {
  source_file_name: string;
  source_type: JcImportSource;
  source_job_number: string;
  source_job_name: string;
  cost_code: string;
  description: string;
  period_to_date: string | null;
  job_to_date: string | null;
  original_estimate: string | null;
  revised_estimate: string | null;
  percent_of_estimate: string | null;
  actual_percent_complete_or_col_g: string | null;
  date_reported_or_col_h: string | null;
  current_variance_or_col_i: string | null;
  projected_cost_to_complete: string | null;
  projected_percent_of_estimate: string | null;
  raw_row_number: number;
  raw_cost_total_row_number: number | null;
  raw_values: Record<string, unknown>;
  warnings: string[];
};

export type JcReportTotals = {
  period_to_date: string | null;
  job_to_date: string | null;
  original_estimate: string | null;
  revised_estimate: string | null;
  percent_of_estimate: string | null;
  current_variance: string | null;
  projected_cost_to_complete: string | null;
};

export type JcPreviewFile = {
  import_id: number;
  file_name: string;
  source_type: JcImportSource;
  suggested_section_id: number | null;
  metadata: Record<string, string>;
  parsed_line_count: number;
  parsed_total_original_estimate: string;
  parsed_total_revised_estimate: string;
  parsed_total_job_to_date: string;
  report_total_original_estimate: string | null;
  report_total_revised_estimate: string | null;
  report_total_job_to_date: string | null;
  report_totals?: JcReportTotals;
  parsed_totals?: JcReportTotals;
  validation_warnings: Array<Record<string, unknown>>;
  lines: JcParsedLine[];
  header_row_number?: number;
  sheet_name?: string;
  status: BudgetImport['status'];
  error_message?: string;
  classification_reason?: string;
  extraction_method?: string;
  suggested_source?: JcImportSource;
  requires_source_confirmation?: boolean;
};

export type JcProposedAction =
  'create' | 'update' | 'skip_duplicate' | 'conflict';

export type JcProposedChange = {
  file_name: string;
  import_id: number;
  source_type: JcImportSource;
  cost_code: string;
  description: string;
  import_source: JcImportSource;
  suggested_section_id: number | null;
  existing_budget_line_id: number | null;
  existing_section_id: number | null;
  existing_values: Record<string, unknown> | null;
  imported_values: Record<string, unknown>;
  action: JcProposedAction;
  warnings: string[];
};

export type JcBatchPreviewResponse = {
  project_id: number;
  files: JcPreviewFile[];
  combined_summary: {
    total_files: number;
    total_lines: number;
    total_original_estimate: string;
    total_revised_estimate: string;
    total_job_to_date: string;
  };
  proposed_changes: JcProposedChange[];
};

export type JcBatchPreviewRequest = {
  project: number;
  files: Array<{
    file_url: string;
    file_name: string;
    import_source_override?: JcImportSource;
    sha256?: string;
  }>;
};

export type JcPreviewJobStatus =
  | 'queued'
  | 'processing'
  | 'succeeded'
  | 'failed'
  | 'expired';

export type JcPreviewJob = {
  job_id: string;
  project_id: number;
  status: JcPreviewJobStatus;
  error_code: string;
  error_message: string;
  expires_at: string | null;
  result: JcBatchPreviewResponse | null;
};

export type JcBatchCommitLine = {
  import_id: number;
  cost_code: string;
  section: number | null;
  import_source: JcImportSource;
};

export type JcBatchCommitRequest = {
  project: number;
  import_ids: number[];
  mode: JcCommitMode;
  selected_lines: JcBatchCommitLine[];
  selected_sources?: JcImportSource[];
  confirm_destructive?: boolean;
};

export type JcBatchCommitResponse = {
  created: number;
  updated: number;
  skipped: number;
  conflicts: number;
  deleted: number;
  errors: Array<Record<string, unknown>>;
  version_id: number | null;
  version_number: number | null;
};

// ---------------------------------------------------------------------
// Statistics
// ---------------------------------------------------------------------
export type StatisticsResponse = {
  kpis: Record<string, string | null>;
  section_split: Array<{
    section_code: string;
    section_name: string;
    revised: string;
    planned: string;
    projected_revised: string;
    job_to_date: string;
    variance: string;
  }>;
  top_variance: Array<{
    id: number;
    cost_code: string;
    cost_code_name: string;
    section_code: string;
    current_variance: string;
  }>;
  top_spend: Array<{
    id: number;
    cost_code: string;
    cost_code_name: string;
    section_code: string;
    job_to_date: string;
  }>;
  budget_over_time: Array<{
    version_number: number;
    snapshot_at: string;
    reason: string;
    total_revised_estimate: string;
  }>;
  change_order_impact_over_time: Array<{
    version_number: number;
    snapshot_at: string;
    reason: string;
    approved_co_total: string;
    pending_co_total: string;
    allocated_approved_co_total: string;
    unallocated_approved_co_total: string;
    planned_pending_co_total: string;
    unplanned_pending_co_total: string;
    co_budget_total: string;
    co_profit_total: string;
  }>;
};
